import React, { useState, useEffect } from 'react';
import { Save, Check, AlertCircle, Lock, IdCard } from 'lucide-react';
import crm from '../crm/api';

// Self-service profile. One screen for both roles — the endpoint scopes to the
// caller, so an admin editing themselves and a rep editing themselves run the
// same code. Admins change OTHER people on the Employees page, not here.
//
// Email and profile slug are shown but not editable: the slug is printed on
// physical NFC cards, and email is the login identity. Both need an admin.
const FIELDS = [
  { key: 'name', label: 'Full name', placeholder: 'Sathya Shankar', hint: 'Shown on your card and microsite.' },
  { key: 'designation', label: 'Designation', placeholder: 'Senior Marketing Executive', hint: 'The gold line under your name on the card.' },
  { key: 'whatsapp', label: 'Mobile / WhatsApp number', placeholder: '98765 43210', hint: 'Used by BOTH the Call and WhatsApp buttons. Leave blank and prospects reach the shared company line instead of you.' },
  { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/in/…', hint: 'Blank keeps the DFC company page.' },
];

export default function MyProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [state, setState] = useState('loading'); // loading | ready | saving | error
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'err', text }

  useEffect(() => {
    crm.get('/auth/me')
      .then(({ data }) => {
        setMe(data.user);
        setForm({
          name: data.user.name || '', designation: data.user.designation || '',
          whatsapp: data.user.whatsapp || '', linkedin: data.user.linkedin || '',
          cardTheme: data.user.cardTheme || 'green',
        });
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setMsg(null); };

  const save = async () => {
    setState('saving'); setMsg(null);
    try {
      const { data } = await crm.put('/auth/me', form);
      setMe(data.user);
      // Keep the cached copy in step — the sidebar and card screen read from it.
      localStorage.setItem('crm_user', JSON.stringify(data.user));
      setMsg({ kind: 'ok', text: 'Profile saved.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || 'Could not save. Try again.' });
    } finally { setState('ready'); }
  };

  const savePassword = async () => {
    if (pw.newPassword !== pw.confirm) return setMsg({ kind: 'err', text: 'The two new passwords do not match.' });
    if (pw.newPassword.length < 6) return setMsg({ kind: 'err', text: 'New password must be at least 6 characters.' });
    setState('saving'); setMsg(null);
    try {
      await crm.put('/auth/me', { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      setMsg({ kind: 'ok', text: 'Password changed. It applies the next time you sign in.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || 'Could not change the password.' });
    } finally { setState('ready'); }
  };

  if (state === 'loading') return <div className="text-[13px] text-[var(--muted)] pt-2">Loading your profile…</div>;
  if (state === 'error') return <div className="text-[13px] text-[var(--muted)] pt-2">Could not load your profile. Refresh and try again.</div>;

  const input = 'w-full px-3.5 py-2.5 rounded-xl text-[14px] bg-[var(--card)] text-[var(--text)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--acc)]/40 focus:border-[var(--acc)]';
  const cardBox = 'bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-6';

  return (
    <div className="space-y-5 max-w-[860px] pt-1">
      <div>
        <h2 className="text-[20px] font-extrabold text-[var(--heading)] tracking-tight">My profile</h2>
        <p className="text-[13px] text-[var(--muted)]">Your details as prospects see them on your card and microsite.</p>
      </div>

      {msg && (
        <div className="flex items-center gap-2 text-[13px] rounded-xl px-3.5 py-2.5 border"
             style={msg.kind === 'ok'
               ? { background: 'rgba(26,128,70,.10)', borderColor: 'rgba(26,128,70,.35)', color: '#1a8046' }
               : { background: 'rgba(185,28,28,.10)', borderColor: 'rgba(185,28,28,.35)', color: '#b91c1c' }}>
          {msg.kind === 'ok' ? <Check size={15} /> : <AlertCircle size={15} />}{msg.text}
        </div>
      )}

      <div className={cardBox}>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === 'linkedin' || f.key === 'whatsapp' ? 'sm:col-span-2' : ''}>
              <label className="block text-[12px] font-bold text-[var(--text)] mb-1.5">{f.label}</label>
              <input className={input} value={form[f.key] ?? ''} onChange={set(f.key)} placeholder={f.placeholder} />
              <p className="text-[11.5px] text-[var(--muted)] mt-1">{f.hint}</p>
            </div>
          ))}

          <div>
            <label className="block text-[12px] font-bold text-[var(--text)] mb-1.5">Card colourway</label>
            <select className={input} value={form.cardTheme || 'green'} onChange={set('cardTheme')}>
              <option value="green">Green (house style)</option>
              <option value="charcoal">Charcoal</option>
            </select>
            <p className="text-[11.5px] text-[var(--muted)] mt-1">Applies to your digital card.</p>
          </div>
        </div>

        <button onClick={save} disabled={state === 'saving'}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[14px] font-bold text-white disabled:opacity-60"
          style={{ background: 'linear-gradient(145deg,var(--acc),var(--acc2))' }}>
          <Save size={15} />{state === 'saving' ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* Locked fields — visible so nobody hunts for them, with who to ask. */}
      <div className={cardBox}>
        <div className="flex items-center gap-2 mb-3">
          <IdCard size={16} className="text-[var(--muted)]" />
          <h3 className="text-[14px] font-extrabold text-[var(--heading)]">Set by your admin</h3>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 text-[13px]">
          <div><div className="text-[11.5px] text-[var(--muted)] font-semibold uppercase tracking-wide mb-1">Login email</div>
            <div className="text-[var(--text)] font-semibold break-all">{me.email}</div></div>
          <div><div className="text-[11.5px] text-[var(--muted)] font-semibold uppercase tracking-wide mb-1">Card address</div>
            <div className="text-[var(--text)] font-semibold break-all">{me.profileSlug || '— not set —'}</div></div>
          <div><div className="text-[11.5px] text-[var(--muted)] font-semibold uppercase tracking-wide mb-1">Role</div>
            <div className="text-[var(--text)] font-semibold capitalize">{me.role}</div></div>
        </div>
        <p className="text-[11.5px] text-[var(--muted)] mt-3">
          The card address is encoded on your printed NFC card — changing it would stop that card working, so only
          an admin can. Ask on the Employees screen.
        </p>
      </div>

      <div className={cardBox}>
        <div className="flex items-center gap-2 mb-3">
          <Lock size={16} className="text-[var(--muted)]" />
          <h3 className="text-[14px] font-extrabold text-[var(--heading)]">Change password</h3>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className="block text-[12px] font-bold text-[var(--text)] mb-1.5">Current password</label>
            <input type="password" autoComplete="current-password" className={input} value={pw.currentPassword}
                   onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} /></div>
          <div><label className="block text-[12px] font-bold text-[var(--text)] mb-1.5">New password</label>
            <input type="password" autoComplete="new-password" className={input} value={pw.newPassword}
                   onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} /></div>
          <div><label className="block text-[12px] font-bold text-[var(--text)] mb-1.5">Confirm new password</label>
            <input type="password" autoComplete="new-password" className={input} value={pw.confirm}
                   onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
        </div>
        <button onClick={savePassword} disabled={state === 'saving' || !pw.currentPassword || !pw.newPassword}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[14px] font-bold border border-[var(--border)] text-[var(--text)] disabled:opacity-50">
          <Lock size={15} />Update password
        </button>
      </div>
    </div>
  );
}
